import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, IconButton, InputAdornment, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import { AnalyticsService } from '@localstack/services';
import { SeatHolderActivity, SortDirection, UsageInsightSortField } from '@localstack/types';
import { Dropdown } from '@localstack/ui';

import { debounce } from 'lodash';

import moment from 'moment';

import { UsageTable } from './UsageTable';

export const PAGE_SIZE = 50;

export const RANGE_OPTIONS: Map<number, string> = new Map([
  [7, 'Last 7 days'],
  [30, 'Last 30 days'],
  [90, 'Last 90 days'],
]);

type UsageDataControllerProps = {
  fetchData: typeof AnalyticsService.getUsageInsights;
  isLoading: boolean;
};

export const UsageInsightView = ({ fetchData, isLoading }: UsageDataControllerProps): ReactElement => {
  const [searchString, setSearchString] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<UsageInsightSortField>(UsageInsightSortField.LAST_ACTIVE_DATE);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC);
  const [rangeDays, setRangeDays] = useState<number>(7);
  const [page, setPage] = useState(0);

  const fromDate = moment().subtract(rangeDays, 'days').format('YYYY-MM-DD');
  const toDate = moment().format('YYYY-MM-DD');

  const [pageRows, setPageRows] = useState<SeatHolderActivity[]>([]);
  const [total, setTotal] = useState({ count: 0, filterKey: '' });

  const filterKey = `${searchString}-${rangeDays}`;
  const resetScrollKey = `${filterKey}-${sortBy}-${sortDirection}`;

  const [prevResetScrollKey, setPrevResetScrollKey] = useState(resetScrollKey);

  if (prevResetScrollKey !== resetScrollKey) {
    setPrevResetScrollKey(resetScrollKey);
    setPage(0);
  }

  const debouncedOnSearchChange = useMemo(() => debounce((value: string) => setSearchString(value), 500), []);
  useEffect(() => () => debouncedOnSearchChange.cancel(), [debouncedOnSearchChange]);

  const fetchFunction = useCallback(async () => {
    const data = await fetchData(fromDate, toDate, searchString, sortBy, sortDirection, page, PAGE_SIZE);
    return data ?? [];
  }, [fromDate, toDate, searchString, sortBy, sortDirection, page]);

  const loadData = async () => {
    const data = await fetchFunction();
    setPageRows(data);
    setTotal({ count: data.at(0)?.total_rows ?? 0, filterKey });
  };

  const appendData = async () => {
    const data = await fetchFunction();
    setPageRows((prev) => [...prev, ...data]);
  };

  useEffect(() => {
    if (page === 0) {
      loadData();
      return;
    }
    appendData();
  }, [fetchFunction]);

  const hasMore = pageRows.length < total.count;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={2}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search members by name or email"
          value={searchInput}
          onChange={(e) => {
            const value = e.target.value;
            setSearchInput(value);
            debouncedOnSearchChange(value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchString !== '' && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    setSearchInput('');
                    setSearchString('');
                  }}
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: 320 }}
        />

        <Dropdown
          size="small"
          stopPropagation
          label={
            <Stack direction="row" spacing={0.5}>
              <Typography variant="body1" color="text.tertiary">
                Range
              </Typography>
              <Typography variant="body1">{RANGE_OPTIONS.get(rangeDays) ?? 'Last 7 days'}</Typography>
            </Stack>
          }
        >
          {(onClose) =>
            Array.from(RANGE_OPTIONS.entries()).map(([days, label]) => (
              <MenuItem
                key={label}
                value={String(days)}
                onClick={() => {
                  setRangeDays(days ?? 7);
                  onClose();
                }}
              >
                <Typography variant="body1">{label}</Typography>
              </MenuItem>
            ))
          }
        </Dropdown>
      </Stack>

      <UsageTable
        data={pageRows}
        totalRows={total.count}
        showHeader={total.count > 0 && total.filterKey === filterKey}
        isLoading={isLoading && page === 0}
        hasMore={hasMore}
        isLoadingMore={isLoading}
        onLoadMore={() => setPage((prev) => prev + 1)}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortChange={(field, direction) => {
          setSortBy(field);
          setSortDirection(direction);
        }}
        resetScrollKey={resetScrollKey}
      />
    </Box>
  );
};
